import { useState, useRef, useCallback } from 'react';
import { refreshHighlightRules } from '../utils/aceHighlightingUtils';
import { createTextMatchRegex } from '../utils/regexUtils';
import { IAceEditor } from 'react-ace/lib/types';

export type SetupHighlightingListeners = (
  editor: IAceEditor,
  autocompleteValuesRef: React.RefObject<string[]>,
) => void;

export const useAutocompleteHighlighting = () => {
  const [selectedAutocompleteValues, setSelectedAutocompleteValues] = useState<string[]>([]);
  const autocompleteTexts = useRef(new Set<string>());
  const lastInsertedText = useRef<string | null>(null);
  const editorRef = useRef<IAceEditor | null>(null);

  const addAutocompleteText = (text: string) => {
    if (text !== lastInsertedText.current) {
      lastInsertedText.current = text;
      autocompleteTexts.current.add(text);
      return true;
    }
    return false;
  };

  const updateHighlightRules = (editor: IAceEditor) => {
    const allLines = editor.session.getDocument().getAllLines().join('\n');
    const activeTexts = new Set<string>();

    // Check which autocomplete texts are still intact
    autocompleteTexts.current.forEach((text) => {
      const regex = createTextMatchRegex(text);
      if (regex.test(allLines)) {
        activeTexts.add(text);
      }
    });

    // Update the set to only include active texts
    autocompleteTexts.current.clear();
    activeTexts.forEach((text) => autocompleteTexts.current.add(text));

    refreshHighlightRules(editor, Array.from(activeTexts));

    // Update state
    setSelectedAutocompleteValues(Array.from(activeTexts));
  };

  /**
   * Seeds the highlighting logic with specific terms (used on load)
   */
  const syncHighlightedTerms = useCallback((terms: string[]) => {
    autocompleteTexts.current.clear();
    terms.forEach((t) => autocompleteTexts.current.add(t));

    if (editorRef.current) {
      updateHighlightRules(editorRef.current);
    }
  }, []);

  const setupHighlightingListeners = (
    editor: IAceEditor,
    autocompleteValuesRef: React.RefObject<string[]>,
  ) => {
    // Listen for command execution to catch autocomplete insertions
    editorRef.current = editor;

    editor.commands.on('afterExec', (e: { command: { name: string }; args: string }) => {
      if (e.command.name === 'insertstring' && e.args) {
        const currentValues = autocompleteValuesRef.current || [];
        const insertedText = e.args.trim();

        // Check if this was an autocomplete insertion
        if (currentValues.includes(insertedText)) {
          if (addAutocompleteText(insertedText)) {
            updateHighlightRules(editor);
          }
        }
      }
    });

    // Update highlights on input
    editor.on('input', () => {
      setTimeout(() => {
        updateHighlightRules(editor);
      }, 100);
    });

    // Initial run to catch seeded terms
    updateHighlightRules(editor);
  };

  return {
    selectedAutocompleteValues,
    setSelectedAutocompleteValues,
    setupHighlightingListeners,
    syncHighlightedTerms,
  };
};
